import { Capacitor } from '@capacitor/core'
import { JeepSqlite } from 'jeep-sqlite/dist/components/jeep-sqlite'
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite'
import { DataSource } from 'typeorm'
import { Project } from '@/classes/entities/Project'
import { ProjectDate } from '@/classes/entities/ProjectDate'
import { AddProjectsTable1678791203278 } from '@/classes/migrations/1678791203278-AddProjectsTable'
import { AddProjectDatesTable1678791241996 } from '@/classes/migrations/1678791241996-AddProjectDatesTable'
import { AddDeletedAtToProjectsTable1686914235000 } from '@/classes/migrations/1686914235000-AddDeletedAtToProjectsTable'
import { AddContactsTable1741264258468 } from '@/classes/migrations/1741264258468-AddContactsTable'
import { AddPersistenceTable1748736000000 } from '@/classes/migrations/1748736000000-AddPersistenceTable'
import { EntityEventSubscriber } from '@/classes/entities/listener/EntityEventSubscriber'
import { Contact } from '../entities/Contact'
import { Persistence } from '../entities/Persistence'
import { report } from '@/classes/manager/ExceptionReportingManager'
import { DebugService } from '@/api/client'

export default class DatabaseManager {
  static databaseName = 'clockin_app'
  static appDataSource: DataSource | null = null
  static connectionWrapper: SQLiteConnection | null = null
  static platform: string = Capacitor.getPlatform()
  static async setup() {
    if (!DatabaseManager.connectionWrapper) {
      DatabaseManager.connectionWrapper = new SQLiteConnection(CapacitorSQLite)
    }
    if (DatabaseManager.platform === 'web') {
      await DatabaseManager.setupWebDatabase()
    }
    // close possibly existing connection before setting up a new one
    try {
      console.log('close existing database connection')
      await CapacitorSQLite.closeConnection({
        database: DatabaseManager.databaseName,
      })
    } catch (e) {
      // this throws when no connection is open
      console.log(e)
    }

    DatabaseManager.appDataSource = DatabaseManager.createAppDataSource()
    await DatabaseManager.appDataSource.initialize()
    await DatabaseManager.appDataSource.runMigrations()
  }

  static async setupWebDatabase() {
    if (!DatabaseManager.connectionWrapper)
      throw new Error('Cannot setup web database without connection wrapper')
    console.log('setupWebDatabase')
    if (DatabaseManager.platform === 'web') {
      const jeepSqliteExists = !!document.querySelector('jeep-sqlite')
      console.log('jeepSqliteExists', jeepSqliteExists)
      if (!jeepSqliteExists) {
        customElements.define('jeep-sqlite', JeepSqlite)
        await customElements.whenDefined('jeep-sqlite')
        const jeepSqlite = document.createElement('jeep-sqlite')
        document.body.appendChild(jeepSqlite)
        console.log('now jeepSqlite Exists', jeepSqlite)
      }
      console.log('init sqlite webstore')
      await DatabaseManager.connectionWrapper.initWebStore()
      console.log('sqlite webstore initialized')
    }
  }

  static async makeConnectionConsistentBetweenNativeAndJS() {
    console.log('makeConnectionConsistentBetweenNativeAndJS')
    await CapacitorSQLite.checkConnectionsConsistency({
      dbNames: [], // i.e. "i expect no connections to be open"
    }).catch((e) => {
      // the plugin throws an error when closing connections. we can ignore
      // that since it is expected behaviour
      console.log(e)
      return {}
    })
  }

  static createAppDataSource() {
    console.log('createAppDataSource')
    return new DataSource({
      name: 'projectConnection',
      type: 'capacitor',
      driver: DatabaseManager.connectionWrapper,
      database: DatabaseManager.databaseName,
      entities: [Project, ProjectDate, Contact, Persistence],
      migrations: [
        AddProjectsTable1678791203278,
        AddProjectDatesTable1678791241996,
        AddDeletedAtToProjectsTable1686914235000,
        AddContactsTable1741264258468,
        AddPersistenceTable1748736000000,
      ],
      logging: ['error', 'schema'],
      synchronize: false,
      migrationsRun: false,
      subscribers:
        Capacitor.getPlatform() === 'web' ? [EntityEventSubscriber] : [],
    })
  }
  static async persistMemoryDatabase() {
    if (!DatabaseManager.connectionWrapper)
      throw new Error(
        'Cannot persist memory database without connection wrapper',
      )
    const platform = Capacitor.getPlatform()
    if (platform === 'web') {
      console.log('persisting memory database')
      await DatabaseManager.connectionWrapper.saveToStore('clockin_app')
    }
  }

  static async clearAll() {
    console.log('clearing all Database tables')
    if (!DatabaseManager.appDataSource)
      throw new Error('Trying to clear db but no datasource is set')
    for (const { name } of DatabaseManager.appDataSource.entityMetadatas) {
      try {
        await DatabaseManager.appDataSource.getRepository(name).clear()
      } catch (e) {
        console.error(e)
      }
    }
  }

  static async exportForDebugging() {
    try {
      const exportData = await CapacitorSQLite.exportToJson({
        database: DatabaseManager.databaseName,
        jsonexportmode: 'full',
      })
      const filename = `clockin_app_state_export_${new Date().toISOString()}.json`
      const urlResponse = await DebugService.generateDebugUploadUrl({
        filename,
        content_type: 'application/json',
      })
      const uploadUrl = urlResponse.data.upload_url
      await fetch(uploadUrl, {
        method: 'PUT',
        body: JSON.stringify(exportData),
        headers: {
          'Content-Type': 'application/json',
        },
      })
    } catch (error) {
      report(error as Error)
    }
  }

  static async sync(entities: { sync: () => void }[]) {
    void DatabaseManager.setup()
    for (const entity of entities) {
      await entity.sync()
    }
  }
}
